import {
  POSITION_LEVELS,
  COMMUNICATION_FREQUENCY,
  COMMUNICATION_SCOPE,
  COMMUNICATION_STYLE,
  COMMUNICATION_CULTURE,
  STRATEGIC_INFLUENCE,
  RESULT_VISIBILITY,
  QUALITY_CONTROL
} from './constants';

/**
 * Determines the position level based on job title
 * @param {string} positionTitle - The job title
 * @returns {number} Position level (1-6)
 */
export function getPositionLevel(positionTitle) {
  const title = positionTitle.toLowerCase();
  
  if (title.includes('ceo') || title.includes('chief')) return POSITION_LEVELS.CEO;
  if (title.includes('head')) return POSITION_LEVELS.HEAD;
  if (title.includes('principal')) return POSITION_LEVELS.PRINCIPAL;
  if (title.includes('staff')) return POSITION_LEVELS.STAFF;
  if (title.includes('senior')) return POSITION_LEVELS.SENIOR;
  if (title.includes('specialist')) return POSITION_LEVELS.SPECIALIST;
  if (title.includes('mid')) return POSITION_LEVELS.MID;
  if (title.includes('junior')) return POSITION_LEVELS.JUNIOR;
  
  // Default to mid-level if no keywords match
  return POSITION_LEVELS.MID;
}

/**
 * Determines if position has leadership responsibility
 * @param {string} positionGroupId - The position group identifier
 * @param {string} positionTitle - The job title
 * @returns {boolean}
 */
export function hasLeadershipResponsibility(positionGroupId, positionTitle) {
  if (!positionGroupId) return false;
  
  const hasLeadershipGroup = positionGroupId.includes('führung') || 
                            positionGroupId.includes('Geschäftsführung') ||
                            positionGroupId.includes('Leitung');
  
  const hasLeadershipTitle = positionTitle.toLowerCase().includes('head') ||
                           positionTitle.toLowerCase().includes('lead') ||
                           positionTitle.toLowerCase().includes('manager') ||
                           positionTitle.toLowerCase().includes('director');
  
  return hasLeadershipGroup || hasLeadershipTitle;
}

/**
 * Maps position to communication characteristics
 * @param {number} level - Position level
 * @param {boolean} hasLeadership - Has leadership responsibility
 * @returns {object} Communication settings
 */
export function getCommunicationSettings(level, hasLeadership) {
  const settings = {
    frequency: COMMUNICATION_FREQUENCY.LITTLE,
    scope: COMMUNICATION_SCOPE.SMALL_GROUP,
    style: COMMUNICATION_STYLE.FACTUAL,
    culture: COMMUNICATION_CULTURE.INTERNATIONAL
  };
  
  // High-level positions or leadership roles
  if (level >= 5 || hasLeadership) {
    settings.frequency = COMMUNICATION_FREQUENCY.MUCH;
    settings.style = COMMUNICATION_STYLE.MOTIVATING;
    
    if (hasLeadership) {
      settings.scope = COMMUNICATION_SCOPE.DEPARTMENT;
    }
    
    if (level === 6) {
      settings.style = COMMUNICATION_STYLE.INSPIRING;
      settings.frequency = COMMUNICATION_FREQUENCY.DOMINANT;
    }
  }
  // Senior/Staff level
  else if (level >= 3) {
    settings.frequency = COMMUNICATION_FREQUENCY.LITTLE;
    settings.style = COMMUNICATION_STYLE.FACTUAL;
  }
  // Junior/Mid level
  else {
    settings.frequency = COMMUNICATION_FREQUENCY.LITTLE;
    settings.style = COMMUNICATION_STYLE.FACTUAL;
  }
  
  return settings;
}

/**
 * Maps position to complexity characteristics
 * @param {number} level - Position level
 * @param {boolean} hasLeadership - Has leadership responsibility
 * @returns {object} Complexity settings
 */
export function getComplexitySettings(level, hasLeadership) {
  const settings = {
    strategicInfluence: STRATEGIC_INFLUENCE.MOSTLY_OPERATIONAL,
    resultVisibility: RESULT_VISIBILITY.DAYS,
    qualityControl: QUALITY_CONTROL.EASY
  };
  
  // High-level positions or leadership roles
  if (level >= 5 || hasLeadership) {
    settings.strategicInfluence = STRATEGIC_INFLUENCE.MIXED;
    settings.resultVisibility = RESULT_VISIBILITY.MONTHS;
    settings.qualityControl = QUALITY_CONTROL.KPI_BASED;
    
    if (level === 6) {
      settings.strategicInfluence = STRATEGIC_INFLUENCE.MOSTLY_STRATEGIC;
      settings.qualityControl = QUALITY_CONTROL.RESULT_BASED;
    }
  }
  // Senior/Staff level
  else if (level >= 3) {
    settings.strategicInfluence = STRATEGIC_INFLUENCE.OPERATIONAL_WITH_STRATEGIC;
    settings.resultVisibility = RESULT_VISIBILITY.WEEKS;
    settings.qualityControl = QUALITY_CONTROL.DIFFICULT;
  }
  // Junior/Mid level
  else {
    settings.strategicInfluence = STRATEGIC_INFLUENCE.MOSTLY_OPERATIONAL;
    settings.resultVisibility = RESULT_VISIBILITY.DAYS;
    settings.qualityControl = QUALITY_CONTROL.EASY;
  }
  
  return settings;
}

/**
 * Generates intelligent defaults for a position
 * @param {string} positionTitle - The job title
 * @param {string} positionGroupId - The position group identifier
 * @returns {object} Default communication and complexity arrays
 */
export function generatePositionDefaults(positionTitle, positionGroupId) {
  const level = getPositionLevel(positionTitle);
  const hasLeadership = hasLeadershipResponsibility(positionGroupId, positionTitle);
  
  const communicationSettings = getCommunicationSettings(level, hasLeadership);
  const complexitySettings = getComplexitySettings(level, hasLeadership);
  
  // Build communication array (matching existing format)
  const kommunikation = [
    communicationSettings.frequency,
    communicationSettings.style,
    communicationSettings.scope,
    communicationSettings.culture
  ];
  
  // Build complexity array (matching existing format)
  const komplexität = [
    complexitySettings.strategicInfluence,
    complexitySettings.resultVisibility,
    complexitySettings.qualityControl
  ];
  
  return { 
    kommunikation, 
    komplexität,
    level,
    hasLeadership,
    hasGeneratedDefaults: true
  };
}